import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CardActionArea,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  Receipt as InvoiceIcon,
  Info as InfoIcon,
  ArrowUpward as SortAscIcon,
  ArrowDownward as SortDescIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import {
  People as UsersIcon,
  FlightTakeoff as TripsIcon,
  TrendingUp as GrowthIcon,
  AttachMoney as RevenueIcon,
  Psychology as AIIcon,
  Map as MapsIcon,
  CalendarToday as CalendarIcon,
  BarChart as ChartIcon,
  Refresh as RefreshIcon,
  Business as PartnerIcon,
  Inventory as PackageIcon,
  Download as DownloadIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import { SingleImageUploader } from '../components/ImageUploader';
import AdminPartnerPackagesDialog from '../components/AdminPartnerPackagesDialog';
import AdminChrome, {
  adminOutlineBtnSx,
  adminSolidBtnSx,
  adminStatPaperSx,
  adminSurfaceCardSx,
} from '../components/AdminChrome';
import { DOCUMENT } from '../constants/documentTheme';
import {
  initialSortState,
  nextSortState,
  sortRecords,
  toTimestamp,
  formatListDate,
} from '../utils/adminListSort';
import {
  INTERNAL_EMAIL_DOMAINS,
  countInternalAccounts,
  withoutInternalAccounts,
} from '../utils/internalAccounts';
import { groupAdminTrips, filterAdminTripGroups, isAdminTripGroup } from '../utils/adminTripGroups';
import { startPartnerSupportSession } from '../utils/partnerSupportSession';

// Staff logins, demo travelers and old test trips sit on our own domain, so the
// admin lists offer to leave them out.
const INTERNAL_DOMAIN_LABEL = INTERNAL_EMAIL_DOMAINS.map((domain) => `@${domain}`).join(', ');

const HideInternalToggle = ({ checked, onChange, count, testId }) => (
  <FormControlLabel
    sx={{ ml: 0, mb: 2 }}
    control={(
      <Switch
        size="small"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        inputProps={{ 'data-testid': testId }}
      />
    )}
    label={(
      <Typography variant="caption" color="text.secondary">
        {`Hide ${INTERNAL_DOMAIN_LABEL} accounts${count > 0 ? ` (${count})` : ''}`}
      </Typography>
    )}
  />
);

const ADMIN_PLAN_OPTIONS = [
  { id: 'free', name: 'Starter' },
  { id: 'pro', name: 'Advisor Pro' },
  { id: 'group', name: 'Group Pro' },
  { id: 'agency', name: 'Agency' },
  { id: 'bespoke', name: 'Enterprise' }
];

export const adminPlanLabel = (tier) => (
  ADMIN_PLAN_OPTIONS.find((plan) => plan.id === tier)?.name || 'Starter'
);

export const adminPlanChipColor = (tier) => {
  if (tier === 'bespoke') return 'warning';
  if (tier === 'agency') return 'secondary';
  if (tier === 'group') return 'info';
  if (tier === 'pro') return 'primary';
  return 'default';
};

// The people list modal serves registered users, daily active users and
// subscribers. Each one names the dates it shows on a row and can sort by.
// "Last active" is the stamp from real app usage — a login date would be
// misleading, since user tokens last a year with a sliding refresh.
export const ADMIN_LIST_SORT_LABELS = {
  name: 'Name',
  email: 'Email',
  joined: 'Joined',
  active: 'Last active',
  subscribed: 'Subscribed',
};

export const ADMIN_PEOPLE_MODALS = {
  users: {
    title: 'Registered Users',
    endpoint: '/admin/export/user-emails',
    listKey: 'users',
    defaultSort: 'joined',
    sortFields: ['name', 'email', 'joined', 'active'],
    rowDates: [
      { field: 'joined' },
      { field: 'active', withTime: true, emptyLabel: 'No activity yet' },
    ],
  },
  active: {
    title: 'Daily Active Users',
    endpoint: '/admin/active-users',
    listKey: 'users',
    defaultSort: 'active',
    sortFields: ['name', 'email', 'active', 'joined'],
    rowDates: [
      { field: 'active', withTime: true, emptyLabel: 'No activity yet' },
      { field: 'joined' },
    ],
  },
  newUsers7d: {
    title: 'New Users (7 days)',
    endpoint: '/admin/new-users?days=7',
    listKey: 'users',
    defaultSort: 'joined',
    sortFields: ['name', 'email', 'joined', 'active'],
    rowDates: [
      { field: 'joined' },
      { field: 'active', withTime: true, emptyLabel: 'No activity yet' },
    ],
  },
  active7d: {
    title: 'Active Users (7 days)',
    endpoint: '/admin/active-users?hours=168',
    listKey: 'users',
    defaultSort: 'active',
    sortFields: ['name', 'email', 'active', 'joined'],
    rowDates: [
      { field: 'active', withTime: true, emptyLabel: 'No activity yet' },
      { field: 'joined' },
    ],
  },
  subscribers: {
    title: 'Newsletter Subscribers',
    endpoint: '/admin/export/subscriber-emails',
    listKey: 'subscribers',
    defaultSort: 'subscribed',
    sortFields: ['name', 'email', 'subscribed'],
    rowDates: [{ field: 'subscribed' }],
    hideName: true,
  },
};

const ADMIN_LIST_SORT_KINDS = { joined: 'date', active: 'date', subscribed: 'date' };

const personLabel = (item) => item?.name || item?.email || '';

export const adminListItemDate = (item, field) => {
  if (field === 'active') return item?.lastActiveAt || null;
  if (field === 'subscribed') return item?.subscribedAt || null;
  return item?.createdAt || null;
};

const ADMIN_LIST_ACCESSORS = {
  name: personLabel,
  email: (item) => item?.email || '',
  joined: (item) => toTimestamp(item?.createdAt),
  active: (item) => toTimestamp(item?.lastActiveAt),
  subscribed: (item) => toTimestamp(item?.subscribedAt),
};

export const adminListSortDefault = (field) => initialSortState(field, ADMIN_LIST_SORT_KINDS);

export const formatAdminListDate = formatListDate;

export const sortAdminEmailList = (items, field = 'joined', direction = 'desc') => (
  sortRecords(items, ADMIN_LIST_ACCESSORS, field, direction, personLabel)
);

// Columns of the all-trips modal, in display order.
export const ADMIN_TRIP_COLUMNS = [
  { field: 'id', label: 'ID', align: 'left' },
  { field: 'title', label: 'Trip', align: 'left' },
  { field: 'destination', label: 'Destination', align: 'left' },
  { field: 'creator', label: 'Created by', align: 'left' },
  { field: 'members', label: 'Members', align: 'right' },
  { field: 'start', label: 'Departs', align: 'left' },
  { field: 'created', label: 'Created', align: 'left' },
];

const ADMIN_TRIP_SORT_KINDS = { created: 'date', start: 'date', members: 'number', id: 'number' };

const ADMIN_TRIP_ACCESSORS = {
  id: (trip) => Number(trip?.id) || 0,
  title: (trip) => trip?.title || '',
  destination: (trip) => trip?.destination || '',
  creator: (trip) => trip?.creatorName || trip?.creatorEmail || '',
  members: (trip) => Number(trip?.memberCount) || 0,
  start: (trip) => toTimestamp(trip?.startDate),
  created: (trip) => toTimestamp(trip?.createdAt),
};

export const adminTripSortDefault = (field) => initialSortState(field, ADMIN_TRIP_SORT_KINDS);

export const sortAdminTrips = (trips, field = 'created', direction = 'desc') => (
  sortRecords(trips, ADMIN_TRIP_ACCESSORS, field, direction, (trip) => trip?.title || '')
);

// Every column after ID, shared by group rows and the traveler trips under them.
const AdminTripCells = ({ trip, badge = null, compact = false }) => (
  <>
    <TableCell>
      <Typography
        component="a"
        href={`/trips/${trip.id}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: DOCUMENT.oxblood,
          fontWeight: compact ? 500 : 600,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {trip.title || `Trip ${trip.id}`}
      </Typography>
      {badge}
      {!compact && trip.managedByPartner && (
        <Chip label={trip.partnerName || 'Partner'} size="small" sx={{ ml: 1, borderRadius: 0 }} />
      )}
    </TableCell>
    <TableCell>{trip.destination || '—'}</TableCell>
    <TableCell>
      <Typography variant="body2">{trip.creatorName}</Typography>
      <Typography variant="caption" sx={{ color: DOCUMENT.ink3 }}>
        {trip.creatorEmail}
      </Typography>
    </TableCell>
    <TableCell align="right">{(Number(trip.memberCount) || 0).toLocaleString()}</TableCell>
    <TableCell>{trip.startDate ? formatAdminListDate(trip.startDate) : '—'}</TableCell>
    <TableCell>{formatAdminListDate(trip.createdAt)}</TableCell>
  </>
);

const emptyAdminBranding = {
  logo: '',
  brandColor: '',
  brandBanner: '',
  brandNavInk: '',
  brandInk: '',
  brandCanvas: '',
  brandReplyTo: '',
  brandSenderDomain: '',
};

const brandingFromAdminPartner = (partner) => ({
  logo: partner?.logo || '',
  brandColor: partner?.brandColor || '',
  brandBanner: partner?.brandBanner || '',
  brandNavInk: partner?.brandNavInk || '',
  brandInk: partner?.brandInk || '',
  brandCanvas: partner?.brandCanvas || '',
  brandReplyTo: partner?.brandReplyTo || '',
  brandSenderDomain: partner?.brandSenderDomain || '',
});

const adminDetailPaperSx = {
  p: 2,
  mb: 0,
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
};

const adminSectionTitleSx = {
  fontSize: 11,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink3,
  fontWeight: 600,
  mb: 1.25,
};

const adminFieldLabelSx = {
  fontSize: 10.5,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink3,
  display: 'block',
  mb: 0.35,
};

const adminMetricPaperSx = {
  p: 2,
  textAlign: 'center',
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.rule}`,
  borderRadius: 0,
  boxShadow: 'none',
};

const AdminPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalType, setEmailModalType] = useState('users'); // 'users' or 'subscribers'
  const [emailList, setEmailList] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSearch, setEmailSearch] = useState('');
  const [emailSort, setEmailSort] = useState(() => adminListSortDefault('joined'));
  const [emailSource, setEmailSource] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Shared by the people and trip lists: our own accounts are noise in both.
  const [hideInternal, setHideInternal] = useState(true);

  // All trips modal state
  const [tripsModalOpen, setTripsModalOpen] = useState(false);
  const [tripsModalTitle, setTripsModalTitle] = useState('All Trips');
  const [tripList, setTripList] = useState([]);
  const [tripsListLoading, setTripsListLoading] = useState(false);
  const [tripSearch, setTripSearch] = useState('');
  const [tripSort, setTripSort] = useState(() => adminTripSortDefault('created'));
  const [expandedTripGroups, setExpandedTripGroups] = useState(() => new Set());

  // Recent payments modal (Recent Activity → Revenue)
  const [paymentsModalOpen, setPaymentsModalOpen] = useState(false);
  const [paymentList, setPaymentList] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // AI Concierge / White Glove requesters modal
  const [aiRequestsModalOpen, setAiRequestsModalOpen] = useState(false);
  const [aiRequestsTitle, setAiRequestsTitle] = useState('');
  const [aiRequestList, setAiRequestList] = useState([]);
  const [aiRequestsLoading, setAiRequestsLoading] = useState(false);
  const [aiRequestSearch, setAiRequestSearch] = useState('');

  const [aiChatLogsOpen, setAiChatLogsOpen] = useState(false);
  const [aiChatLogs, setAiChatLogs] = useState([]);
  const [aiChatLogsLoading, setAiChatLogsLoading] = useState(false);
  const [aiChatLogSearch, setAiChatLogSearch] = useState('');

  // Partner detail modal state
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [packagesPartner, setPackagesPartner] = useState(null);
  const [invoiceMonth, setInvoiceMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [planDraft, setPlanDraft] = useState('free');
  const [planSaving, setPlanSaving] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [planSaved, setPlanSaved] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [statusSaved, setStatusSaved] = useState(false);
  const [brandingDraft, setBrandingDraft] = useState(emptyAdminBranding);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingError, setBrandingError] = useState(null);
  const [brandingSaved, setBrandingSaved] = useState(false);
  const [portalStarting, setPortalStarting] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [marketplaceBookings, setMarketplaceBookings] = useState([]);
  const [marketplaceLoading, setMarketplaceLoading] = useState(false);

  useEffect(() => {
    fetchAdminStats();
    fetchMarketplaceBookings();
  }, []);

  const fetchMarketplaceBookings = async () => {
    setMarketplaceLoading(true);
    try {
      const response = await api.get('/admin/marketplace-bookings');
      setMarketplaceBookings(response.data?.bookings || []);
    } catch (err) {
      console.error('Failed to load marketplace bookings:', err);
    } finally {
      setMarketplaceLoading(false);
    }
  };

  const marketplaceAction = async (id, action) => {
    await api.post(`/admin/marketplace-bookings/${id}/${action}`);
    await fetchMarketplaceBookings();
  };

  const openEmailModal = async (type) => {
    const config = ADMIN_PEOPLE_MODALS[type] || ADMIN_PEOPLE_MODALS.users;
    setEmailModalType(type);
    setEmailModalOpen(true);
    setEmailLoading(true);
    setEmailSearch('');
    setEmailSort(adminListSortDefault(config.defaultSort));
    setEmailSource(null);

    try {
      const response = await api.get(config.endpoint);
      setEmailList(response.data?.[config.listKey] || []);
      setEmailSource(response.data?.source || null);
    } catch (err) {
      console.error('Failed to fetch emails:', err);
      setEmailList([]);
    } finally {
      setEmailLoading(false);
    }
  };

  const openTripsModal = async ({ days = null, title = 'All Trips' } = {}) => {
    setTripsModalOpen(true);
    setTripsModalTitle(title);
    setTripsListLoading(true);
    setTripSearch('');
    setTripSort(adminTripSortDefault('created'));

    try {
      const response = await api.get(days ? `/admin/trips?days=${days}` : '/admin/trips');
      setTripList(response.data?.trips || []);
    } catch (err) {
      console.error('Failed to fetch trips:', err);
      setTripList([]);
    } finally {
      setTripsListLoading(false);
    }
  };

  const openPaymentsModal = async ({ days = 7 } = {}) => {
    setPaymentsModalOpen(true);
    setPaymentsLoading(true);
    try {
      const response = await api.get(`/admin/recent-payments?days=${days}`);
      setPaymentList(response.data?.payments || []);
    } catch (err) {
      console.error('Failed to fetch recent payments:', err);
      setPaymentList([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const openAiRequestsModal = async ({ service = null, status = null, title } = {}) => {
    setAiRequestsModalOpen(true);
    setAiRequestsTitle(title || t('adminAiUsage.recentRequests'));
    setAiRequestsLoading(true);
    setAiRequestSearch('');
    try {
      const params = new URLSearchParams();
      if (service) params.set('service', service);
      if (status) params.set('status', status);
      const query = params.toString();
      const response = await api.get(`/admin/ai-service-requests${query ? `?${query}` : ''}`);
      setAiRequestList(response.data?.requests || []);
    } catch (err) {
      console.error('Failed to fetch AI service requests:', err);
      setAiRequestList([]);
    } finally {
      setAiRequestsLoading(false);
    }
  };

  const openAiChatLogs = async () => {
    setAiChatLogsOpen(true);
    setAiChatLogsLoading(true);
    setAiChatLogSearch('');
    try {
      const response = await api.get('/admin/ai-chat-logs?limit=100');
      setAiChatLogs(response.data?.logs || []);
    } catch (err) {
      console.error('Failed to fetch AI chat logs:', err);
      setAiChatLogs([]);
    } finally {
      setAiChatLogsLoading(false);
    }
  };

  const filteredAiChatLogs = useMemo(() => {
    const search = aiChatLogSearch.trim().toLowerCase();
    if (!search) return aiChatLogs;
    return aiChatLogs.filter((row) => (
      (row.actorEmail || '').toLowerCase().includes(search)
      || (row.actorName || '').toLowerCase().includes(search)
      || (row.prompt || '').toLowerCase().includes(search)
      || (row.status || '').toLowerCase().includes(search)
      || (row.blockReason || '').toLowerCase().includes(search)
    ));
  }, [aiChatLogs, aiChatLogSearch]);

  const filteredAiRequests = useMemo(() => {
    const search = aiRequestSearch.trim().toLowerCase();
    if (!search) return aiRequestList;
    return aiRequestList.filter((req) => (
      (req.userName || '').toLowerCase().includes(search)
      || (req.userEmail || '').toLowerCase().includes(search)
      || (req.tripTitle || '').toLowerCase().includes(search)
      || (req.status || '').toLowerCase().includes(search)
      || (req.service || '').toLowerCase().includes(search)
    ));
  }, [aiRequestList, aiRequestSearch]);

  const filteredEmails = useMemo(() => {
    const search = emailSearch.toLowerCase();
    const people = hideInternal ? withoutInternalAccounts(emailList) : emailList;
    const matches = people.filter(item =>
      item.email.toLowerCase().includes(search) ||
      (item.name && item.name.toLowerCase().includes(search))
    );
    return sortAdminEmailList(matches, emailSort.field, emailSort.direction);
  }, [emailList, emailSearch, emailSort, hideInternal]);

  const internalEmailCount = useMemo(() => countInternalAccounts(emailList), [emailList]);

  const toggleEmailSort = (field) => {
    setEmailSort((prev) => nextSortState(prev, field, ADMIN_LIST_SORT_KINDS));
  };

  const groupedTrips = useMemo(() => groupAdminTrips(tripList), [tripList]);

  const filteredTrips = useMemo(() => {
    const matches = filterAdminTripGroups(groupedTrips, { search: tripSearch, hideInternal });
    return sortAdminTrips(matches, tripSort.field, tripSort.direction);
  }, [groupedTrips, tripSearch, tripSort, hideInternal]);

  const toggleTripGroup = (tripId) => {
    setExpandedTripGroups((prev) => {
      const next = new Set(prev);
      if (next.has(tripId)) next.delete(tripId);
      else next.add(tripId);
      return next;
    });
  };

  const internalTripCount = useMemo(
    () => countInternalAccounts(tripList, (trip) => trip?.creatorEmail),
    [tripList]
  );

  const toggleTripSort = (field) => {
    setTripSort((prev) => nextSortState(prev, field, ADMIN_TRIP_SORT_KINDS));
  };

  const peopleModalConfig = ADMIN_PEOPLE_MODALS[emailModalType] || ADMIN_PEOPLE_MODALS.users;

  const peopleSecondaryText = (item) => {
    const dates = (peopleModalConfig.rowDates || []).map(({ field, withTime, emptyLabel }) => {
      const value = adminListItemDate(item, field);
      if (!value) return emptyLabel || `No ${ADMIN_LIST_SORT_LABELS[field].toLowerCase()} date`;
      return `${ADMIN_LIST_SORT_LABELS[field]} ${formatAdminListDate(value, { withTime })}`;
    });

    if (peopleModalConfig.hideName) return dates.join(' · ');
    return [item.name || 'No name', ...dates].join(' · ');
  };

  const copyAllEmails = () => {
    const emails = filteredEmails.map(item => item.email).join('\n');
    navigator.clipboard.writeText(emails);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const openPartnerModal = (partner) => {
    setSelectedPartner(partner);
    setPartnerModalOpen(true);
    setInvoiceData(null);
    setPlanDraft(partner.planTier || 'free');
    setPlanError(null);
    setPlanSaved(false);
    setStatusError(null);
    setStatusSaved(false);
    setBrandingDraft(brandingFromAdminPartner(partner));
    setBrandingError(null);
    setBrandingSaved(false);
    setPortalStarting(false);
    setPortalError(null);
  };

  const openPartnerPortal = useCallback(async () => {
    const partnerId = selectedPartner?.id;
    if (!partnerId || portalStarting) return;
    setPortalStarting(true);
    setPortalError(null);
    try {
      await startPartnerSupportSession(partnerId);
      setPartnerModalOpen(false);
      navigate('/partner/dashboard');
    } catch (err) {
      console.error('Failed to start support session:', err);
      setPortalError(t('partnerSupport.startFailed'));
    } finally {
      setPortalStarting(false);
    }
  }, [navigate, portalStarting, selectedPartner?.id, t]);

  const mergePartnerUpdate = (partial) => {
    setSelectedPartner((prev) => (prev ? { ...prev, ...partial } : prev));
    setStats((prev) => {
      if (!prev?.partnerMetrics?.partnerBreakdown) return prev;
      return {
        ...prev,
        partnerMetrics: {
          ...prev.partnerMetrics,
          partnerBreakdown: prev.partnerMetrics.partnerBreakdown.map((partner) => (
            partner.id === partial.id
              ? { ...partner, ...partial }
              : partner
          ))
        }
      };
    });
  };

  const savePartnerPlan = async () => {
    if (!selectedPartner) return;
    setPlanSaving(true);
    setPlanError(null);
    setPlanSaved(false);
    try {
      const response = await api.post(`/admin/partners/${selectedPartner.id}/plan`, {
        tier: planDraft
      });
      const planTier = response.data.planTier;
      const planName = response.data.planName;
      mergePartnerUpdate({ id: selectedPartner.id, planTier, planName });
      setPlanSaved(true);
    } catch (err) {
      setPlanError(err.response?.data?.error || 'Failed to update plan');
    } finally {
      setPlanSaving(false);
    }
  };

  const savePartnerStatus = async (nextActive) => {
    if (!selectedPartner) return;
    setStatusSaving(true);
    setStatusError(null);
    setStatusSaved(false);
    try {
      const response = await api.patch(`/admin/partners/${selectedPartner.id}/status`, {
        isActive: nextActive,
      });
      mergePartnerUpdate({
        id: selectedPartner.id,
        isActive: response.data.isActive,
        directoryListed: response.data.directoryListed,
      });
      setStatusSaved(true);
    } catch (err) {
      setStatusError(err.response?.data?.error || t('adminPage.statusSaveError'));
    } finally {
      setStatusSaving(false);
    }
  };

  const handleBrandingChange = (event) => {
    const { name, value } = event.target;
    setBrandingDraft((prev) => ({ ...prev, [name]: value }));
    setBrandingSaved(false);
  };

  const savePartnerBranding = async () => {
    if (!selectedPartner) return;
    setBrandingSaving(true);
    setBrandingError(null);
    setBrandingSaved(false);
    try {
      const response = await api.put(`/admin/partners/${selectedPartner.id}/branding`, brandingDraft);
      const branding = response.data.branding || {};
      mergePartnerUpdate({
        id: selectedPartner.id,
        logo: branding.logo || '',
        brandColor: branding.brandColor || '',
        brandBanner: branding.brandBanner || '',
        brandNavInk: branding.brandNavInk || '',
        brandInk: branding.brandInk || '',
        brandCanvas: branding.brandCanvas || '',
        brandReplyTo: branding.brandReplyTo || '',
        brandSenderDomain: branding.brandSenderDomain || '',
      });
      setBrandingDraft(brandingFromAdminPartner({
        logo: branding.logo,
        brandColor: branding.brandColor,
        brandBanner: branding.brandBanner,
        brandNavInk: branding.brandNavInk,
        brandInk: branding.brandInk,
        brandCanvas: branding.brandCanvas,
        brandReplyTo: branding.brandReplyTo,
        brandSenderDomain: branding.brandSenderDomain,
      }));
      setBrandingSaved(true);
    } catch (err) {
      setBrandingError(err.response?.data?.error || t('adminPage.brandingSaveError'));
    } finally {
      setBrandingSaving(false);
    }
  };

  const generateInvoice = async () => {
    if (!selectedPartner || !invoiceMonth) return;
    
    setInvoiceLoading(true);
    try {
      const [year, month] = invoiceMonth.split('-');
      const response = await api.get(`/admin/commission-report/${year}/${month}`);
      
      // Filter for this specific partner
      const partnerCheckout = response.data.directCheckout.partners.find(
        p => p.partnerId === selectedPartner.id
      ) || { bookingCount: 0, totalRevenue: 0, commission: 0, payoutDue: 0 };
      
      const partnerInquiry = response.data.inquiryExternal.partners.find(
        p => p.partnerId === selectedPartner.id
      ) || { bookingCount: 0, totalRevenue: 0, commissionOwed: 0 };
      
      setInvoiceData({
        period: response.data.period,
        directCheckout: partnerCheckout,
        inquiryExternal: partnerInquiry,
        netBalance: (partnerCheckout.payoutDue || 0) - (partnerInquiry.commissionOwed || 0)
      });
    } catch (err) {
      console.error('Failed to generate invoice:', err);
      setInvoiceData({ error: 'Failed to generate invoice' });
    } finally {
      setInvoiceLoading(false);
    }
  };

  const fetchAdminStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Test simple endpoint first
      console.log('Testing admin endpoint...');
      const testResponse = await api.get('/admin/test');
      console.log('Test response:', testResponse.data);
      
      // If test works, fetch full stats with cache busting
      if (testResponse.data.message === 'Admin test endpoint working') {
        console.log('Admin test successful, fetching full stats...');
        const response = await api.get(`/admin/stats?t=${Date.now()}`);
        console.log('Stats response:', response.data);
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('CORS')) {
        setError('CORS error: Unable to connect to backend. Please check if backend is running on port 5002.');
      } else {
        setError(err.response?.data?.error || 'Failed to fetch admin statistics');
      }
    } finally {
      setLoading(false);
    }
  };


  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminChrome title={t('adminNav.overviewTitle')} subtitle={t('adminNav.overviewSubtitle')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
          <CircularProgress size={40} sx={{ color: DOCUMENT.oxblood }} />
          <Typography sx={{ ml: 2, color: DOCUMENT.ink3 }}>
            Loading admin statistics...
          </Typography>
        </Box>
      </AdminChrome>
    );
  }

  if (error) {
    return (
      <AdminChrome title={t('adminNav.overviewTitle')} subtitle={t('adminNav.overviewSubtitle')}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 0 }}>
          {error}
        </Alert>
      </AdminChrome>
    );
  }

  if (!stats) {
    return (
      <AdminChrome title={t('adminNav.overviewTitle')} subtitle={t('adminNav.overviewSubtitle')}>
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          No statistics available.
        </Alert>
      </AdminChrome>
    );
  }

  const StatCard = ({ title, value, icon, color = 'primary', subtitle, onClick, testId }) => {
    const body = (
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{
            p: 1.25,
            bgcolor: DOCUMENT.ivory,
            border: `1px solid ${DOCUMENT.rule}`,
            color: DOCUMENT.oxblood,
            mr: 2,
          }}>
            {icon}
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
            {title}
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '2rem', color: DOCUMENT.ink, mb: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: DOCUMENT.ink3 }}>
            {subtitle}
          </Typography>
        )}
        {onClick && (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: DOCUMENT.oxblood, fontSize: 11, letterSpacing: '0.11em', textTransform: 'uppercase' }}
          >
            View all
          </Typography>
        )}
      </CardContent>
    );

    return (
      <Card sx={{ ...adminSurfaceCardSx, height: '100%' }}>
        {onClick ? (
          <CardActionArea
            onClick={onClick}
            data-testid={testId}
            aria-label={`View all ${title}`}
            sx={{ height: '100%', alignItems: 'stretch', textAlign: 'left' }}
          >
            {body}
          </CardActionArea>
        ) : body}
      </Card>
    );
  };

  return (
    <AdminChrome
      title={t('adminNav.overviewTitle')}
      subtitle={t('adminNav.overviewSubtitle')}
      actions={(
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchAdminStats}
          disabled={loading}
          sx={adminOutlineBtnSx}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      )}
    >

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={formatNumber(stats.totalUsers)}
            icon={<UsersIcon />}
            color="primary"
            subtitle="All registered users"
            onClick={() => openEmailModal('users')}
            testId="admin-stat-total-users"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Daily Active Users"
            value={formatNumber(stats.dailyActiveUsers)}
            icon={<GrowthIcon />}
            color="success"
            subtitle="Users active in last 24 hours"
            onClick={() => openEmailModal('active')}
            testId="admin-stat-active-users"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Trips"
            value={formatNumber(stats.totalTrips)}
            icon={<TripsIcon />}
            color="info"
            subtitle="All trips created"
            onClick={() => openTripsModal()}
            testId="admin-stat-total-trips"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={formatCurrency(stats.totalRevenue)}
            icon={<RevenueIcon />}
            color="warning"
            subtitle="All payments processed"
          />
        </Grid>
      </Grid>

      {/* Detailed Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* AI Usage */}
        <Grid item xs={12} md={6}>
          <Card sx={adminSurfaceCardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AIIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  AI Usage Statistics
                </Typography>
                <Tooltip title="Based on actual payment transactions for AI Concierge and White Glove services" arrow>
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 1, cursor: 'help' }} />
                </Tooltip>
                <Button
                  size="small"
                  onClick={openAiChatLogs}
                  sx={{ ml: 'auto' }}
                  data-testid="admin-ai-chat-logs-open"
                >
                  {t('adminAiUsage.viewChatPrompts')}
                </Button>
              </Box>
              <Grid container spacing={2}>
                {[
                  {
                    key: 'total',
                    label: t('adminAiUsage.totalRequests'),
                    value: formatNumber(stats.aiUsage?.totalRequests),
                    onClick: () => openAiRequestsModal({ title: t('adminAiUsage.allRequestsTitle') }),
                    testId: 'admin-ai-total-requests',
                  },
                  {
                    key: 'costs',
                    label: t('adminAiUsage.totalCosts'),
                    value: formatCurrency(stats.aiUsage?.totalCosts),
                    onClick: () => openAiRequestsModal({ title: t('adminAiUsage.allRequestsTitle') }),
                    testId: 'admin-ai-total-costs',
                  },
                  {
                    key: 'concierge',
                    label: t('adminAiUsage.conciergeRequests'),
                    value: formatNumber(stats.aiUsage?.conciergeRequests),
                    onClick: () => openAiRequestsModal({
                      service: 'ai_concierge',
                      title: t('adminAiUsage.conciergeTitle'),
                    }),
                    testId: 'admin-ai-concierge-requests',
                  },
                  {
                    key: 'whiteGlove',
                    label: t('adminAiUsage.whiteGloveRequests'),
                    value: formatNumber(stats.aiUsage?.whiteGloveRequests),
                    onClick: () => openAiRequestsModal({
                      service: 'white_glove',
                      title: t('adminAiUsage.whiteGloveTitle'),
                    }),
                    testId: 'admin-ai-white-glove-requests',
                  },
                ].map((metric) => (
                  <Grid item xs={6} key={metric.key}>
                    <Box
                      component="button"
                      type="button"
                      onClick={metric.onClick}
                      data-testid={metric.testId}
                      aria-label={`View ${metric.label}`}
                      sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        background: 'transparent',
                        p: 0,
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover .admin-ai-metric-value': { color: DOCUMENT.oxblood },
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {metric.label}
                      </Typography>
                      <Typography className="admin-ai-metric-value" variant="h5" sx={{ fontWeight: 600 }}>
                        {metric.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: DOCUMENT.oxblood, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10 }}
                      >
                        {t('adminAiUsage.view')}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Status Breakdown — click a status to open the filtered requester list */}
              {stats.aiUsage?.statusBreakdown && Object.keys(stats.aiUsage.statusBreakdown).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    {t('adminAiUsage.statusBreakdown')}
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(stats.aiUsage.statusBreakdown).map(([status, data]) => (
                      <Grid item xs={12} key={status}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => openAiRequestsModal({
                            status,
                            title: t('adminAiUsage.statusTitle', { status }),
                          })}
                          data-testid={`admin-ai-status-${status}`}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            p: 1,
                            backgroundColor: 'grey.50',
                            borderRadius: 1,
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            '&:hover': { backgroundColor: 'grey.100' },
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                              {status}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {data.conciergeCount} concierge, {data.whiteGloveCount} white glove
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {data.count} requests
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(data.cost)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Google Maps Usage */}
        <Grid item xs={12} md={6}>
          <Card sx={adminSurfaceCardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <MapsIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Google Maps Usage
                </Typography>
                <Tooltip title="Estimated based on trip and itinerary activity. Check Google Cloud Console for actual API costs." arrow>
                  <Chip label="Estimate" size="small" color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Maps Requests
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatNumber(stats.mapsUsage?.totalRequests)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Maps Costs
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatCurrency(stats.mapsUsage?.totalCosts)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Avg Cost per Request
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatCurrency(stats.mapsUsage?.avgCostPerRequest)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Request Types
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {Object.keys(stats.mapsUsage?.requestsByType || {}).length}
                  </Typography>
                </Grid>
              </Grid>
              {/* Maps Request Types Breakdown */}
              {stats.mapsUsage?.requestsByType && Object.keys(stats.mapsUsage.requestsByType).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Request Types Breakdown:
                  </Typography>
                  {Object.entries(stats.mapsUsage.requestsByType).map(([type, data]) => (
                    <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {type.replace('_', ' ')}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {data.count} requests ({formatCurrency(data.cost)})
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card sx={adminSurfaceCardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CalendarIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Recent Activity
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {[
                  {
                    key: 'newUsers',
                    label: t('adminRecentActivity.newUsers'),
                    value: formatNumber(stats.recentActivity?.newUsers7Days),
                    onClick: () => openEmailModal('newUsers7d'),
                    testId: 'admin-recent-new-users',
                  },
                  {
                    key: 'newTrips',
                    label: t('adminRecentActivity.newTrips'),
                    value: formatNumber(stats.recentActivity?.newTrips7Days),
                    onClick: () => openTripsModal({ days: 7, title: t('adminRecentActivity.newTripsTitle') }),
                    testId: 'admin-recent-new-trips',
                  },
                  {
                    key: 'activeUsers',
                    label: t('adminRecentActivity.activeUsers'),
                    value: formatNumber(stats.recentActivity?.activeUsers7Days),
                    onClick: () => openEmailModal('active7d'),
                    testId: 'admin-recent-active-users',
                  },
                  {
                    key: 'revenue',
                    label: t('adminRecentActivity.revenue'),
                    value: formatCurrency(stats.recentActivity?.revenue7Days),
                    onClick: () => openPaymentsModal({ days: 7 }),
                    testId: 'admin-recent-revenue',
                  },
                ].map((metric) => (
                  <Grid item xs={6} key={metric.key}>
                    <Box
                      component="button"
                      type="button"
                      onClick={metric.onClick}
                      data-testid={metric.testId}
                      aria-label={`View ${metric.label}`}
                      sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        background: 'transparent',
                        p: 0,
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover .admin-recent-value': { color: DOCUMENT.oxblood },
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {metric.label}
                      </Typography>
                      <Typography className="admin-recent-value" variant="h5" sx={{ fontWeight: 600 }}>
                        {metric.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: DOCUMENT.oxblood, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10 }}
                      >
                        {t('adminRecentActivity.view')}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Daily Revenue Table */}
      {stats.dailyRevenue && stats.dailyRevenue.length > 0 && (
        <Card sx={{ ...adminSurfaceCardSx, mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ChartIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Daily Revenue (Last 30 Days)
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Transactions</TableCell>
                    <TableCell align="right">Avg. Transaction</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.dailyRevenue.slice(0, 30).map((day, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(day.date)}</TableCell>
                      <TableCell align="right">{formatCurrency(day.revenue)}</TableCell>
                      <TableCell align="right">{formatNumber(day.transactions)}</TableCell>
                      <TableCell align="right">{formatCurrency(day.avgTransaction)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Partner Metrics Section */}
      {stats.partnerMetrics && (
        <Card sx={{ ...adminSurfaceCardSx, mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PartnerIcon sx={{ fontSize: 24, color: DOCUMENT.oxblood, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: DOCUMENT.display }}>
                  Partner Metrics
                </Typography>
              </Box>
              <Button
                onClick={() => navigate('/admin/partner-applications')}
                sx={adminSolidBtnSx}
              >
                Applications ({stats.partnerMetrics.pendingApplications} pending)
              </Button>
            </Box>
            
            {/* Partner Stats Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={4} md={2}>
                <Paper sx={adminStatPaperSx}>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.5rem', color: DOCUMENT.ink, textAlign: 'center' }}>
                    {formatNumber(stats.partnerMetrics.totalPartners)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3, textAlign: 'center', mt: 0.5 }}>Total Partners</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Paper sx={adminStatPaperSx}>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.5rem', color: DOCUMENT.ink, textAlign: 'center' }}>
                    {formatNumber(stats.partnerMetrics.activePartners)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3, textAlign: 'center', mt: 0.5 }}>Active (30d)</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Paper sx={adminStatPaperSx}>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.5rem', color: DOCUMENT.ink, textAlign: 'center' }}>
                    {formatNumber(stats.partnerMetrics.packagesCreated)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3, textAlign: 'center', mt: 0.5 }}>Packages Created</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Paper sx={adminStatPaperSx}>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.5rem', color: DOCUMENT.ink, textAlign: 'center' }}>
                    {formatNumber(stats.partnerMetrics.packagesPublished)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3, textAlign: 'center', mt: 0.5 }}>Published</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Paper sx={adminStatPaperSx}>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.5rem', color: DOCUMENT.ink, textAlign: 'center' }}>
                    {formatNumber(stats.partnerMetrics.packagesSold)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3, textAlign: 'center', mt: 0.5 }}>Packages Sold</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Paper sx={adminStatPaperSx}>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.5rem', color: DOCUMENT.oxblood, textAlign: 'center' }}>
                    {formatCurrency(stats.partnerMetrics.totalCommissionEarned)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3, textAlign: 'center', mt: 0.5 }}>Commission Earned</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Partner Management Table */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              All Partners
            </Typography>
            {stats.partnerMetrics.partnerBreakdown?.length > 0 ? (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5' } }}>
                      <TableCell>Company</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Itineraries</TableCell>
                      <TableCell align="center">Packages</TableCell>
                      <TableCell align="center">Bookings</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Commission</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.partnerMetrics.partnerBreakdown.map((partner) => (
                      <TableRow 
                        key={partner.id} 
                        hover
                        sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{partner.companyName || 'N/A'}</TableCell>
                        <TableCell>{partner.contactName || 'N/A'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {partner.email || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={partner.planName || adminPlanLabel(partner.planTier)}
                            size="small"
                            color={adminPlanChipColor(partner.planTier)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={partner.isActive
                              ? t('adminPage.partnerActive')
                              : t('adminPage.partnerInactive')}
                            sx={{
                              borderRadius: 0,
                              bgcolor: partner.isActive ? DOCUMENT.surface : DOCUMENT.ivory,
                              color: partner.isActive ? DOCUMENT.oxblood : DOCUMENT.ink3,
                              border: `1px solid ${DOCUMENT.ruleHard}`,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={partner.itinerariesCount || 0} 
                            size="small" 
                            color={partner.itinerariesCount > 0 ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={partner.packagesCount}
                            size="small"
                            color={partner.packagesCount > 0 ? 'primary' : 'default'}
                            onClick={() => setPackagesPartner(partner)}
                            aria-label={t('partnerSupport.viewPackages', { company: partner.companyName || '' })}
                            data-testid={`admin-partner-packages-count-${partner.id}`}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={partner.bookingsCount} 
                            size="small" 
                            color={partner.bookingsCount > 0 ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          {formatCurrency(partner.totalRevenue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#c62828' }}>
                          {formatCurrency(partner.commissionEarned)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                          {partner.lastLogin 
                            ? new Date(partner.lastLogin).toLocaleDateString() 
                            : 'Never'}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ViewIcon />}
                            onClick={() => openPartnerModal(partner)}
                          >
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography color="text.secondary">No partners found</Typography>
              </Paper>
            )}
          </CardContent>
        </Card>
      )}

      <Card sx={{ ...adminSurfaceCardSx, mb: 4 }} data-testid="admin-marketplace-bookings">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Marketplace bookings
          </Typography>
          {marketplaceLoading ? (
            <CircularProgress size={24} />
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Partner</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Package</TableCell>
                    <TableCell>Attribution</TableCell>
                    <TableCell>Payment path</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell align="right">Processing</TableCell>
                    <TableCell align="right">Payout</TableCell>
                    <TableCell>Who owes</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {marketplaceBookings.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.partnerName}</TableCell>
                      <TableCell>{row.reference}</TableCell>
                      <TableCell>{row.packageTitle || '—'}</TableCell>
                      <TableCell>{row.attribution}</TableCell>
                      <TableCell>{row.paymentPath === 'dvn_checkout' ? 'In-app' : 'Partner-collected'}</TableCell>
                      <TableCell align="right">{formatCurrency(row.gross)}</TableCell>
                      <TableCell align="right">{row.rate}%</TableCell>
                      <TableCell align="right">{formatCurrency(row.commission)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.processingFee)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.partnerPayout)}</TableCell>
                      <TableCell>{row.whoOwes}</TableCell>
                      <TableCell>
                        {row.whoOwes === 'partner' && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Button size="small" onClick={() => marketplaceAction(row.id, 'invoice')}>Send invoice</Button>
                            <Button size="small" onClick={() => marketplaceAction(row.id, 'mark-collected')}>Mark collected</Button>
                            <Button size="small" onClick={() => marketplaceAction(row.id, 'waive')}>Waive</Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Monthly Payouts Table */}
      {stats.monthlyPayouts?.length > 0 && (
        <Card sx={{ ...adminSurfaceCardSx, mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PackageIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Monthly Partner Payouts
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell>Partner</TableCell>
                    <TableCell>Payment Type</TableCell>
                    <TableCell align="right">Bookings</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Introduction fee</TableCell>
                    <TableCell align="right">Partner Payout (90%)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.monthlyPayouts.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.month}</TableCell>
                      <TableCell>{row.companyName}</TableCell>
                      <TableCell>
                        <Chip 
                          label={row.bookingMethod === 'checkout' ? '💳 Direct' : '✉️ Inquiry'} 
                          size="small" 
                          color={row.bookingMethod === 'checkout' ? 'primary' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="right">{row.bookingCount}</TableCell>
                      <TableCell align="right">{formatCurrency(row.totalRevenue)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.commission)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.partnerPayout)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Email Export Section */}
      <Card sx={{ ...adminSurfaceCardSx, mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <EmailIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Email Lists for Marketing
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {formatNumber(stats.totalUsers)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Registered Users
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    data-testid="admin-view-users-list"
                    onClick={() => openEmailModal('users')}
                  >
                    View List
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    href="/api/admin/export/user-emails?format=csv"
                    target="_blank"
                  >
                    Export CSV
                  </Button>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {formatNumber(stats.totalSubscribers || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Newsletter Subscribers
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={() => openEmailModal('subscribers')}
                  >
                    View List
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    href="/api/admin/export/subscriber-emails?format=csv"
                    target="_blank"
                  >
                    Export CSV
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* User Engagement */}
      <Card sx={adminSurfaceCardSx}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <UsersIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              User Engagement Metrics
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Avg. Trips per User
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {typeof stats.userEngagement?.avgTripsPerUser === 'number' ? stats.userEngagement.avgTripsPerUser.toFixed(2) : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Avg. Itinerary Items per Trip
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {typeof stats.userEngagement?.avgItineraryItemsPerTrip === 'number' ? stats.userEngagement.avgItineraryItemsPerTrip.toFixed(1) : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                User Retention (30 days)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {typeof stats.userEngagement?.retention30Days === 'number' ? stats.userEngagement.retention30Days.toFixed(1) + '%' : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Premium Conversion Rate
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {typeof stats.userEngagement?.premiumConversionRate === 'number' ? stats.userEngagement.premiumConversionRate.toFixed(1) + '%' : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Email List Modal */}
      <Dialog 
        open={emailModalOpen} 
        onClose={() => setEmailModalOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { maxHeight: '80vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {peopleModalConfig.title}
              {!emailLoading && ` (${filteredEmails.length})`}
            </Typography>
            <IconButton onClick={() => setEmailModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {emailSource === 'lifetime' && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
              Nobody was active in the last 24 hours, so this shows everyone who has ever used the app.
            </Alert>
          )}

          {/* Search Bar */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search emails..."
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />

          {/* Sort controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Sort by
            </Typography>
            {peopleModalConfig.sortFields.map((field) => {
              const isActive = emailSort.field === field;
              const label = ADMIN_LIST_SORT_LABELS[field];
              return (
                <Button
                  key={field}
                  size="small"
                  data-testid={`admin-email-sort-${field}`}
                  onClick={() => toggleEmailSort(field)}
                  endIcon={isActive
                    ? (emailSort.direction === 'asc' ? <SortAscIcon fontSize="small" /> : <SortDescIcon fontSize="small" />)
                    : null}
                  sx={{ ...(isActive ? adminSolidBtnSx : adminOutlineBtnSx), px: 1.5, py: 0.5 }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>

          <HideInternalToggle
            checked={hideInternal}
            onChange={setHideInternal}
            count={internalEmailCount}
            testId="admin-hide-internal-users"
          />

          {emailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredEmails.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No emails found
            </Typography>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredEmails.map((item, index) => (
                <ListItem 
                  key={item.id || index} 
                  divider
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    py: 1
                  }}
                >
                  <ListItemText
                    primary={item.email}
                    secondary={peopleSecondaryText(item)}
                    primaryTypographyProps={{ 
                      sx: { fontFamily: 'monospace', fontSize: '0.9rem' } 
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Button
            startIcon={<CopyIcon />}
            onClick={copyAllEmails}
            disabled={filteredEmails.length === 0}
            color={copySuccess ? 'success' : 'primary'}
          >
            {copySuccess ? 'Copied!' : `Copy All (${filteredEmails.length})`}
          </Button>
          <Button onClick={() => setEmailModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* All Trips Modal */}
      <Dialog
        open={tripsModalOpen}
        onClose={() => setTripsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '85vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {tripsModalTitle}
              {!tripsListLoading && ` (${filteredTrips.length})`}
            </Typography>
            <IconButton onClick={() => setTripsModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by trip, destination, creator or id..."
            value={tripSearch}
            onChange={(e) => setTripSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />

          <HideInternalToggle
            checked={hideInternal}
            onChange={setHideInternal}
            count={internalTripCount}
            testId="admin-hide-internal-trips"
          />

          {tripsListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredTrips.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No trips found
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 460 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {ADMIN_TRIP_COLUMNS.map((column) => (
                      <TableCell key={column.field} align={column.align}>
                        <TableSortLabel
                          active={tripSort.field === column.field}
                          direction={tripSort.field === column.field ? tripSort.direction : 'asc'}
                          onClick={() => toggleTripSort(column.field)}
                          data-testid={`admin-trip-sort-${column.field}`}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTrips.map((trip) => {
                    const isGroup = isAdminTripGroup(trip);
                    const travelerCount = trip.travelerTrips.length;
                    const expanded = travelerCount > 0
                      && (expandedTripGroups.has(trip.id) || trip.matchedByTraveler);
                    return (
                      <React.Fragment key={trip.id}>
                        <TableRow hover data-testid={`admin-trip-row-${trip.id}`}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {travelerCount > 0 ? (
                              <IconButton
                                size="small"
                                onClick={() => toggleTripGroup(trip.id)}
                                aria-label={`${expanded ? 'Hide' : 'Show'} traveler trips for ${trip.title || trip.id}`}
                                aria-expanded={expanded}
                                data-testid={`admin-trip-group-toggle-${trip.id}`}
                                sx={{ ml: -1, mr: 0.5 }}
                              >
                                {expanded ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
                              </IconButton>
                            ) : null}
                            {trip.id}
                          </TableCell>
                          <AdminTripCells
                            trip={trip}
                            badge={isGroup ? (
                              <Chip
                                label={travelerCount > 0 ? `Group · ${travelerCount} ${travelerCount === 1 ? 'traveler' : 'travelers'}` : 'Group'}
                                size="small"
                                sx={{ ml: 1, borderRadius: 0 }}
                              />
                            ) : null}
                          />
                        </TableRow>
                        {expanded && trip.travelerTrips.map((traveler) => (
                          <TableRow
                            key={traveler.id}
                            data-testid={`admin-trip-traveler-row-${traveler.id}`}
                            sx={{ bgcolor: DOCUMENT.surface }}
                          >
                            <TableCell sx={{ pl: 6, color: DOCUMENT.ink3 }}>{traveler.id}</TableCell>
                            <AdminTripCells trip={traveler} compact />
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Button
            startIcon={<DownloadIcon />}
            href="/api/admin/trips?format=csv"
            target="_blank"
          >
            Export CSV
          </Button>
          <Button onClick={() => setTripsModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Partner Detail Modal */}
      <Dialog
        open={partnerModalOpen}
        onClose={() => setPartnerModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            bgcolor: DOCUMENT.canvas,
            color: DOCUMENT.ink,
            border: `1px solid ${DOCUMENT.ruleHard}`,
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${DOCUMENT.rule}`, bgcolor: DOCUMENT.surface }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 24, fontWeight: 400, lineHeight: 1.2 }}>
                {selectedPartner?.companyName || 'Partner Details'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mt: 0.5 }}>
                Partner ID: {selectedPartner?.id}
                {selectedPartner?.planName || selectedPartner?.planTier
                  ? ` · ${selectedPartner.planName || adminPlanLabel(selectedPartner.planTier)}`
                  : ''}
              </Typography>
            </Box>
            <IconButton onClick={() => setPartnerModalOpen(false)} size="small" sx={{ color: DOCUMENT.ink3 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: DOCUMENT.canvas, borderColor: DOCUMENT.rule }}>
          {selectedPartner && (
            <>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                  ['Contact Name', selectedPartner.contactName || 'N/A'],
                  ['Email', selectedPartner.email || 'N/A'],
                  ['Phone', selectedPartner.phone || 'N/A'],
                  ['Website', selectedPartner.website || 'N/A'],
                  ['Business Type', selectedPartner.businessType?.replace('_', ' ') || 'N/A'],
                  ['Member Since', selectedPartner.createdAt ? new Date(selectedPartner.createdAt).toLocaleDateString() : 'N/A'],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Paper sx={adminDetailPaperSx}>
                      <Typography sx={adminFieldLabelSx}>{label}</Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: DOCUMENT.ink }}>
                        {label === 'Website' && selectedPartner.website ? (
                          <a
                            href={selectedPartner.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: DOCUMENT.oxblood }}
                          >
                            {selectedPartner.website}
                          </a>
                        ) : value}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Paper sx={adminDetailPaperSx}>
                    <Typography sx={adminFieldLabelSx}>Address</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{selectedPartner.address || 'N/A'}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ ...adminDetailPaperSx, mb: 3 }}>
                <Typography sx={adminSectionTitleSx}>Plan</Typography>
                <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, mb: 2 }}>
                  {t('adminPage.planHelp')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="admin-partner-plan-label">Tier</InputLabel>
                    <Select
                      labelId="admin-partner-plan-label"
                      label="Tier"
                      value={planDraft}
                      onChange={(event) => {
                        setPlanDraft(event.target.value);
                        setPlanSaved(false);
                      }}
                      disabled={planSaving}
                    >
                      {ADMIN_PLAN_OPTIONS.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                          {plan.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    onClick={savePartnerPlan}
                    disabled={planSaving || planDraft === (selectedPartner.planTier || 'free')}
                    sx={adminSolidBtnSx}
                  >
                    {planSaving ? 'Saving…' : 'Save plan'}
                  </Button>
                  <Chip
                    label={selectedPartner.planName || adminPlanLabel(selectedPartner.planTier)}
                    size="small"
                    color={adminPlanChipColor(selectedPartner.planTier)}
                  />
                </Box>
                {planError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{planError}</Alert>
                )}
                {planSaved && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {t('adminPage.planSaved')}
                  </Alert>
                )}
              </Paper>

              <Paper sx={{ ...adminDetailPaperSx, mb: 3 }}>
                <Typography sx={adminSectionTitleSx}>
                  {t('adminPage.partnerBranding')}
                </Typography>
                {selectedPartner.planTier === 'bespoke' ? (
                  <>
                    <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, mb: 2 }}>
                      {t('adminPage.partnerBrandingHelp')}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <SingleImageUploader
                        value={brandingDraft.logo}
                        onChange={(url) => {
                          setBrandingDraft((prev) => ({ ...prev, logo: url || '' }));
                          setBrandingSaved(false);
                        }}
                        preset="gallery"
                        folder="partners/logos"
                        label={t('partnerProfile.companyLogo')}
                        height={120}
                        cropAspect={null}
                      />
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: DOCUMENT.ink3 }}>
                        {t('partnerProfile.logoHint')}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <AdminColorField
                          name="brandBanner"
                          label={t('partnerProfile.brandBanner')}
                          hint={t('partnerProfile.brandBannerHint')}
                          value={brandingDraft.brandBanner}
                          fallback="#000000"
                          disabled={brandingSaving}
                          onChange={handleBrandingChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <AdminColorField
                          name="brandNavInk"
                          label={t('partnerProfile.brandMenu')}
                          hint={t('partnerProfile.brandMenuHint')}
                          value={brandingDraft.brandNavInk}
                          fallback="#F6EFE4"
                          disabled={brandingSaving}
                          onChange={handleBrandingChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <AdminColorField
                          name="brandInk"
                          label={t('partnerProfile.brandInk')}
                          hint={t('partnerProfile.brandInkHint')}
                          value={brandingDraft.brandInk}
                          fallback="#000000"
                          disabled={brandingSaving}
                          onChange={handleBrandingChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <AdminColorField
                          name="brandCanvas"
                          label={t('partnerProfile.brandCanvas')}
                          hint={t('partnerProfile.brandCanvasHint')}
                          value={brandingDraft.brandCanvas}
                          fallback="#FFFFFF"
                          disabled={brandingSaving}
                          onChange={handleBrandingChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <AdminColorField
                          name="brandColor"
                          label={t('partnerProfile.brandColor')}
                          hint={t('partnerProfile.brandColorHint')}
                          value={brandingDraft.brandColor}
                          fallback="#C78E33"
                          disabled={brandingSaving}
                          onChange={handleBrandingChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t('partnerProfile.brandReplyTo')}
                          name="brandReplyTo"
                          type="email"
                          value={brandingDraft.brandReplyTo}
                          onChange={handleBrandingChange}
                          disabled={brandingSaving}
                          helperText={t('partnerProfile.brandReplyToHint')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t('partnerProfile.brandSenderDomain')}
                          name="brandSenderDomain"
                          value={brandingDraft.brandSenderDomain}
                          onChange={handleBrandingChange}
                          disabled={brandingSaving}
                          placeholder="blackopallife.com"
                          helperText={t('partnerProfile.brandSenderDomainHint')}
                        />
                      </Grid>
                    </Grid>
                    <Button
                      onClick={savePartnerBranding}
                      disabled={brandingSaving}
                      sx={{ ...adminSolidBtnSx, mt: 2 }}
                    >
                      {brandingSaving ? t('adminPage.savingBranding') : t('adminPage.saveBranding')}
                    </Button>
                    {brandingError && (
                      <Alert severity="error" sx={{ mt: 2 }}>{brandingError}</Alert>
                    )}
                    {brandingSaved && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        {t('adminPage.brandingSaved')}
                      </Alert>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>
                    {t('adminPage.partnerBrandingLocked')}
                  </Typography>
                )}
              </Paper>

              <Paper sx={{ ...adminDetailPaperSx, mb: 3 }}>
                <Typography sx={adminSectionTitleSx}>{t('adminPage.partnerStatus')}</Typography>
                <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, mb: 2 }}>
                  {t('adminPage.partnerStatusHelp')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(selectedPartner.isActive)}
                        disabled={statusSaving}
                        onChange={(event) => savePartnerStatus(event.target.checked)}
                        data-testid="admin-partner-active-switch"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: DOCUMENT.oxblood },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: DOCUMENT.oxblood,
                          },
                        }}
                      />
                    }
                    label={selectedPartner.isActive
                      ? t('adminPage.partnerActive')
                      : t('adminPage.partnerInactive')}
                  />
                  <Chip
                    size="small"
                    label={selectedPartner.isActive
                      ? t('adminPage.partnerActive')
                      : t('adminPage.partnerInactive')}
                    sx={{
                      borderRadius: 0,
                      bgcolor: selectedPartner.isActive ? DOCUMENT.surface : DOCUMENT.ivory,
                      color: selectedPartner.isActive ? DOCUMENT.oxblood : DOCUMENT.ink3,
                      border: `1px solid ${DOCUMENT.ruleHard}`,
                    }}
                  />
                </Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6} sm={4}>
                    <Typography sx={adminFieldLabelSx}>Terms Accepted</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {selectedPartner.termsAcceptedAt ? new Date(selectedPartner.termsAcceptedAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography sx={adminFieldLabelSx}>Terms Version</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {selectedPartner.termsVersion || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography sx={adminFieldLabelSx}>Last Login</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {selectedPartner.lastLogin ? new Date(selectedPartner.lastLogin).toLocaleDateString() : 'Never'}
                    </Typography>
                  </Grid>
                </Grid>
                {statusError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{statusError}</Alert>
                )}
                {statusSaved && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {selectedPartner.isActive
                      ? t('adminPage.partnerActivated')
                      : t('adminPage.partnerDeactivated')}
                  </Alert>
                )}
              </Paper>

              <Typography sx={{ ...adminSectionTitleSx, mb: 1.5 }}>
                Performance Metrics
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                  [selectedPartner.itinerariesCount || 0, 'Itineraries'],
                  [selectedPartner.packagesCount, 'Total Packages'],
                  [selectedPartner.publishedPackagesCount || 0, 'Published'],
                  [selectedPartner.inquiriesCount || 0, 'Inquiries'],
                  [selectedPartner.bookingsCount, 'Total Bookings'],
                  [selectedPartner.paidBookingsCount || 0, 'Paid Bookings'],
                ].map(([value, label]) => (
                  <Grid item xs={6} sm={4} md={2} key={label}>
                    <Paper sx={adminMetricPaperSx}>
                      <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: 22, color: DOCUMENT.ink }}>
                        {value}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                        {label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography sx={{ ...adminSectionTitleSx, mb: 1.5 }}>
                Financial Summary
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                  [formatCurrency(selectedPartner.totalRevenue), 'Paid booking revenue'],
                  [formatCurrency(selectedPartner.commissionEarned), 'Introduction fee'],
                  [formatCurrency(selectedPartner.totalPayout || 0), 'Partner payout'],
                ].map(([value, label]) => (
                  <Grid item xs={12} sm={4} key={label}>
                    <Paper sx={adminMetricPaperSx}>
                      <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: 22, color: DOCUMENT.ink }}>
                        {value}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                        {label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Paper sx={adminDetailPaperSx}>
                <Typography sx={{ ...adminSectionTitleSx, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InvoiceIcon sx={{ fontSize: 16 }} /> Generate Monthly Invoice
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    type="month"
                    label="Select Month"
                    value={invoiceMonth}
                    onChange={(e) => setInvoiceMonth(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 200 }}
                  />
                  <Button
                    onClick={generateInvoice}
                    disabled={invoiceLoading}
                    startIcon={invoiceLoading ? <CircularProgress size={16} sx={{ color: DOCUMENT.ivory }} /> : <InvoiceIcon />}
                    sx={adminSolidBtnSx}
                  >
                    {invoiceLoading ? 'Generating...' : 'Generate Invoice'}
                  </Button>
                </Box>

                {invoiceData && !invoiceData.error && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1.5, color: DOCUMENT.ink }}>
                      Invoice for {invoiceData.period}
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ ...adminDetailPaperSx, borderColor: DOCUMENT.ruleHard }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 13, color: DOCUMENT.ink, mb: 1 }}>
                            Direct Checkout (We owe partner)
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>Bookings:</Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                              {invoiceData.directCheckout.bookingCount}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>Revenue:</Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                              {formatCurrency(invoiceData.directCheckout.totalRevenue)}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, display: 'block', mb: 1 }}>
                            In-app checkout: we are merchant of record. Marketplace fee is deducted only on DVN-sourced bookings (no $25 min, no $1000 cap). Processing fees apply when we collect. Inquiry/external: partner collected; DVN-sourced fees are invoiced.
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>Introduction fee:</Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                              {formatCurrency(invoiceData.directCheckout.commission)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: `1px solid ${DOCUMENT.rule}` }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Payout Due:</Typography>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: DOCUMENT.oxblood }}>
                              {formatCurrency(invoiceData.directCheckout.payoutDue)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ ...adminDetailPaperSx, borderColor: DOCUMENT.ruleHard }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 13, color: DOCUMENT.ink, mb: 1 }}>
                            Inquiry/External (Partner owes us)
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>Bookings:</Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                              {invoiceData.inquiryExternal.bookingCount}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>Revenue:</Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                              {formatCurrency(invoiceData.inquiryExternal.totalRevenue)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: `1px solid ${DOCUMENT.rule}` }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Commission Owed:</Typography>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: DOCUMENT.oxblood }}>
                              {formatCurrency(invoiceData.inquiryExternal.commissionOwed)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper sx={{
                          ...adminDetailPaperSx,
                          borderColor: DOCUMENT.ruleHard,
                          borderWidth: 2,
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                              Net Balance:
                            </Typography>
                            <Typography sx={{
                              fontFamily: DOCUMENT.display,
                              fontWeight: 600,
                              fontSize: 22,
                              color: DOCUMENT.ink,
                            }}>
                              {invoiceData.netBalance >= 0 ? '+' : ''}{formatCurrency(invoiceData.netBalance)}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mt: 0.5 }}>
                            {invoiceData.netBalance >= 0
                              ? 'We owe the partner this amount'
                              : 'Partner owes us this amount'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {invoiceData?.error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {invoiceData.error}
                  </Alert>
                )}

                {!invoiceData && !invoiceLoading && (
                  <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink3 }}>
                    Select a month and click &quot;Generate Invoice&quot; to see the commission breakdown
                  </Typography>
                )}
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: `1px solid ${DOCUMENT.rule}`,
            bgcolor: DOCUMENT.surface,
            px: 3,
            py: 2,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            {portalError ? (
              <Alert severity="error" sx={{ py: 0.5 }}>
                {portalError}
              </Alert>
            ) : null}
            <Button
              variant="contained"
              startIcon={portalStarting ? <CircularProgress size={14} color="inherit" /> : <SupportAgentIcon />}
              disabled={!selectedPartner?.id || portalStarting}
              onClick={openPartnerPortal}
              data-testid="admin-partner-detail-open-portal"
              sx={adminSolidBtnSx}
            >
              {t('partnerSupport.openPortal')}
            </Button>
          </Box>
          <Button onClick={() => setPartnerModalOpen(false)} sx={adminOutlineBtnSx}>
            {t('partnerSupport.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aiRequestsModalOpen}
        onClose={() => setAiRequestsModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxHeight: '80vh' } }}
        data-testid="admin-ai-requests-dialog"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {aiRequestsTitle}
              {!aiRequestsLoading && ` (${filteredAiRequests.length})`}
            </Typography>
            <IconButton onClick={() => setAiRequestsModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder={t('adminAiUsage.searchPlaceholder')}
            value={aiRequestSearch}
            onChange={(e) => setAiRequestSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
            data-testid="admin-ai-requests-search"
          />
          {aiRequestsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredAiRequests.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('adminAiUsage.noRequests')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {filteredAiRequests.map((req) => (
                <ListItem
                  key={req.id}
                  data-testid={`admin-ai-request-${req.id}`}
                  alignItems="flex-start"
                  sx={{ px: 0, borderBottom: `1px solid ${DOCUMENT.rule}` }}
                >
                  <ListItemText
                    primary={req.userName}
                    secondary={[
                      req.userEmail,
                      t(`adminAiUsage.service.${req.service}`, req.service),
                      req.tripTitle,
                      req.status,
                      formatAdminListDate(req.createdAt, { withTime: true }),
                    ].filter(Boolean).join(' · ')}
                  />
                  <Typography sx={{ fontWeight: 600, flexShrink: 0, ml: 2 }}>
                    {formatCurrency(req.amount)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiRequestsModalOpen(false)} sx={adminOutlineBtnSx}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aiChatLogsOpen}
        onClose={() => setAiChatLogsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '85vh' } }}
        data-testid="admin-ai-chat-logs-dialog"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('adminAiUsage.chatPromptsTitle')}
              {!aiChatLogsLoading && ` (${filteredAiChatLogs.length})`}
            </Typography>
            <IconButton onClick={() => setAiChatLogsOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder={t('adminAiUsage.chatSearchPlaceholder')}
            value={aiChatLogSearch}
            onChange={(e) => setAiChatLogSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
            data-testid="admin-ai-chat-logs-search"
          />
          {aiChatLogsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredAiChatLogs.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('adminAiUsage.noChatPrompts')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {filteredAiChatLogs.map((row) => (
                <ListItem
                  key={row.id}
                  alignItems="flex-start"
                  sx={{ px: 0, borderBottom: `1px solid ${DOCUMENT.rule}`, display: 'block' }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.actorName || row.actorEmail || t('adminAiUsage.unknownActor')}
                    {row.actorEmail ? ` · ${row.actorEmail}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                    {[
                      row.status,
                      row.purpose,
                      row.blockReason,
                      formatAdminListDate(row.createdAt, { withTime: true }),
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {row.prompt || '—'}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiChatLogsOpen(false)} sx={adminOutlineBtnSx}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={paymentsModalOpen}
        onClose={() => setPaymentsModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxHeight: '80vh' } }}
        data-testid="admin-recent-payments-dialog"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('adminRecentActivity.revenueTitle')}
              {!paymentsLoading && ` (${paymentList.length})`}
            </Typography>
            <IconButton onClick={() => setPaymentsModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {paymentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : paymentList.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('adminRecentActivity.noPayments')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {paymentList.map((payment) => (
                <ListItem
                  key={payment.id}
                  data-testid={`admin-recent-payment-${payment.id}`}
                  alignItems="flex-start"
                  sx={{ px: 0, borderBottom: `1px solid ${DOCUMENT.rule}` }}
                >
                  <ListItemText
                    primary={payment.userName}
                    secondary={[
                      payment.userEmail,
                      t(`adminRecentActivity.paymentType.${payment.transactionType}`, payment.transactionType),
                      formatAdminListDate(payment.createdAt, { withTime: true }),
                    ].filter(Boolean).join(' · ')}
                  />
                  <Typography sx={{ fontWeight: 600, flexShrink: 0, ml: 2 }}>
                    {formatCurrency(payment.amount)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentsModalOpen(false)} sx={adminOutlineBtnSx}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <AdminPartnerPackagesDialog
        open={Boolean(packagesPartner)}
        partner={packagesPartner}
        onClose={() => setPackagesPartner(null)}
      />
    </AdminChrome>
  );
};

const AdminColorField = ({ name, label, hint, value, fallback, disabled, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
    <Box
      component="input"
      type="color"
      name={name}
      value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback}
      onChange={onChange}
      disabled={disabled}
      aria-label={label}
      sx={{
        width: 48,
        height: 48,
        p: 0,
        border: '1px solid #c4c4c4',
        bgcolor: 'transparent',
        cursor: 'pointer',
      }}
    />
    <TextField
      fullWidth
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={fallback}
      helperText={hint}
    />
  </Box>
);

export default AdminPage;
